interface ImageScaledInfo {
  isScaled: boolean
  scalingFactor: number
}
interface BgInfoInterface {
  image: any
  renderedBgImageHeight: number
  renderedBgImageWidth: number
  imageScaledInfo: ImageScaledInfo
  url: string
}

const fetchSignedUrl = async (
  selectedFileId,
  trArray = [],
  extraQueryParams = {},
) =>
  fetch(`/api/v2/files/${selectedFileId}/signedUrl`, {
    method: 'POST',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
    },
    body: JSON.stringify({
      tr: trArray,
      extraQueryParams,
    }),
  })

// return request.post(`/api/v2/files/${selectedFileId}/signedUrl`, {
// 	tr : trArray,
// 	extraQueryParams
// })

function loadImageFromURL(imageUrl) {
  return new Promise((resolve, reject) => {
    window.fabric.Image.fromURL(
      imageUrl,
      img => {
        if (!img.getElement()) {
          reject(new Error('Failed to load image'))
          return
        }
        resolve(img)
      },
      { crossOrigin: 'Anonymous' },
    )
  })
}

// get canvas height and width and resize image so that its not streched and is constrained by max original canvas height and width
function resizeCanvasAroundBgImage(bgImage, canvasWidth, canvasHeight) {
  const origBgImageHeight = bgImage.height
  const origBgImageWidth = bgImage.width

  const widthFactor = canvasWidth / origBgImageWidth
  const heightFactor = canvasHeight / origBgImageHeight
  const minFactor = Math.min(widthFactor, heightFactor)

  let updatedBgHeight = origBgImageHeight
  let updatedBgWidth = origBgImageWidth

  const imageScaledInfo = { isScaled: false, scalingFactor: 1 }

  if (canvasWidth < origBgImageWidth || canvasHeight < origBgImageHeight) {
    bgImage.scale(minFactor)
    imageScaledInfo.isScaled = true
    imageScaledInfo.scalingFactor = minFactor
  }

  updatedBgHeight = bgImage.getScaledHeight()
  updatedBgWidth = bgImage.getScaledWidth()

  const updatedCanvasHeight = Math.min(canvasHeight, updatedBgHeight)
  const updatedCanvasWidth = Math.min(canvasWidth, updatedBgWidth)

  return { updatedCanvasHeight, updatedCanvasWidth, imageScaledInfo }
}

async function getSignedUrl(fileId, trArray, fileUpdatedAt) {
  let response
  try {
    response = await fetchSignedUrl(fileId, trArray, {
      updatedAt: new Date(fileUpdatedAt).valueOf(),
    })
    response = response.json()
    return response.data
  } catch (error) {
    console.log(error)
  }
}

function setCanvasDimensions(canvas, dimensions, maxDimensionsAllowed) {
  if (
    dimensions.height <= maxDimensionsAllowed.height &&
    dimensions.width <= maxDimensionsAllowed.width
  ) {
    canvas.setHeight(dimensions.height)
    canvas.setWidth(dimensions.width)
  }
}

function removeObject(canvas, obj) {
  canvas.remove(obj)
}

// functions to be exported
async function setCanvasBackgroundImage(
  fileId,
  canvas,
  imageUrl,
  canvasWidth,
  canvasHeight,
  renderAsBgImg = true,
  alreadyRenderedImage = null,
  trArray = [],
  fileUpdatedAt,
): Promise<BgInfoInterface | undefined> {
  let imageUrlToReturn = imageUrl
  if (alreadyRenderedImage) {
    removeObject(canvas, alreadyRenderedImage)
  }
  let bgImage

  try {
    bgImage = await loadImageFromURL(imageUrl)
  } catch (err) {
    const signedUrl = await getSignedUrl(fileId, trArray, fileUpdatedAt)
    imageUrlToReturn = signedUrl
    bgImage = await loadImageFromURL(signedUrl)
  }

  const { updatedCanvasHeight, updatedCanvasWidth, imageScaledInfo } =
    resizeCanvasAroundBgImage(bgImage, canvasWidth, canvasHeight)

  setCanvasDimensions(
    canvas,
    {
      width: updatedCanvasWidth,
      height: updatedCanvasHeight,
    },
    {
      width: canvasWidth,
      height: canvasHeight,
    },
  )

  bgImage.selectable = false
  bgImage.lockMovementX = true
  bgImage.lockMovementY = true
  bgImage.setControlsVisibility({ mtr: false })

  if (renderAsBgImg) {
    return new Promise<BgInfoInterface>(resolve =>
      canvas.setBackgroundImage(bgImage, () => {
        canvas.renderAll.bind(canvas)
        resolve({
          image: bgImage,
          renderedBgImageHeight: updatedCanvasHeight,
          renderedBgImageWidth: updatedCanvasWidth,
          imageScaledInfo,
          url: imageUrlToReturn,
        })
      }),
    )
  }
  canvas.add(bgImage)
  return {
    image: bgImage,
    renderedBgImageHeight: updatedCanvasHeight,
    renderedBgImageWidth: updatedCanvasWidth,
    imageScaledInfo,
    url: imageUrlToReturn,
  }
}

function addRectangle(canvas, options) {
  const rect = new window.fabric.Rect(options)
  rect.setControlsVisibility({ mtr: false }) // hides rotating icon
  canvas.add(rect)
  return rect
}

function setObjCoords(obj, coords, canvas) {
  if (coords.left) {
    obj.left = Number(Math.round(coords.left))
  }
  if (coords.top) {
    obj.top = Number(Math.round(coords.top))
  }
  if (coords.scaleX) {
    obj.scaleX = parseFloat(coords.scaleX)
  }
  if (coords.scaleY) {
    obj.scaleY = parseFloat(coords.scaleY)
  }
  if (coords.width) {
    obj.width = Number(Math.round(coords.width / obj.scaleX))
  }
  if (coords.height) {
    obj.height = Number(Math.round(coords.height / obj.scaleY))
  }

  obj.setCoords()
  canvas.renderAll()
}

function fetchCoords(obj) {
  return {
    'x-coordinate': Math.round(obj.left),
    'y-coordinate': Math.round(obj.top),
    width: Math.round(obj.width * obj.scaleX),
    height: Math.round(obj.height * obj.scaleY),
  }
}

function addText(canvas, text, options) {
  const textObject = new window.fabric.Text(text, options)
  canvas.add(textObject)
  return textObject
}

function addCropRectangle(canvas, coords) {
  const cropRectangle = addRectangle(canvas, {
    width: coords.width,
    height: coords.height,
    left: coords['x-coordinate'],
    top: coords['y-coordinate'],
    hasBorder: false,
    stroke: 'white',
    strokeWidth: 1,
    hasRotatingPoint: false,
    fill: 'rgba(0,0,0,0.3)',
    strokeUniform: true,
  })

  return cropRectangle
}

function constrainScaledObjToBoundingRect(obj) {
  const boundingRect = obj.getBoundingRect()

  if (boundingRect.left < 0) {
    obj.left = 0
    obj.width = Math.min(
      Number(boundingRect.left) + Number(boundingRect.width),
      Number(obj.canvas.getWidth()),
    )
  } else {
    obj.left = boundingRect.left
    obj.width = boundingRect.width
  }

  if (boundingRect.top < 0) {
    obj.top = 0
    obj.height = Math.min(
      Number(boundingRect.top) + Number(boundingRect.height),
      Number(obj.canvas.getHeight()),
    )
  } else {
    obj.top = boundingRect.top
    obj.height = boundingRect.height
  }

  if (
    Number(boundingRect.left) + Number(boundingRect.width) >
    Number(obj.canvas.getWidth())
  ) {
    obj.width = Number(obj.canvas.getWidth()) - Number(boundingRect.left)
  }

  if (
    Number(boundingRect.top) + Number(boundingRect.height) >
    Number(obj.canvas.getHeight())
  ) {
    obj.height = Number(obj.canvas.getHeight()) - Number(boundingRect.top)
  }

  obj.scaleX = obj._stateProperties.scaleX
  obj.scaleY = obj._stateProperties.scaleY
  obj.strokeWidth = obj._stateProperties.strokeWidth

  obj.setCoords()
  obj.saveState()
}

function constrainMovedObjToBoundingRect(obj) {
  if (
    obj.currentHeight > obj.canvas.height ||
    obj.currentWidth > obj.canvas.width
  ) {
    return
  }
  obj.setCoords()

  if (obj.getBoundingRect().top < 0 || obj.getBoundingRect().left < 0) {
    obj.top = Math.max(obj.top, obj.top - obj.getBoundingRect().top)
    obj.left = Math.max(obj.left, obj.left - obj.getBoundingRect().left)
  }

  if (
    Number(obj.getBoundingRect().top) + Number(obj.getBoundingRect().height) >
      obj.canvas.height ||
    Number(obj.getBoundingRect().left) + Number(obj.getBoundingRect().width) >
      obj.canvas.width
  ) {
    obj.top = Math.min(
      obj.top,
      Number(obj.canvas.height - obj.getBoundingRect().height) +
        Number(obj.top - obj.getBoundingRect().top),
    )
    obj.left = Math.min(
      obj.left,
      Number(obj.canvas.width - obj.getBoundingRect().width) +
        Number(obj.left - obj.getBoundingRect().left),
    )
  }
}

function transformToCanvasCoordValue(value, scalingFactor) {
  // multiply by scaling factor
  return Number(Math.round(parseInt(value) * scalingFactor))
}

function transformToOriginalCoordValue(value, scalingFactor) {
  // divide by scaling factor
  return Number(Math.round(value / scalingFactor))
}

function getOriginalCoords(coords, scalingFactor) {
  return {
    'x-coordinate': transformToOriginalCoordValue(
      coords['x-coordinate'],
      scalingFactor,
    ),
    'y-coordinate': transformToOriginalCoordValue(
      coords['y-coordinate'],
      scalingFactor,
    ),
    width: transformToOriginalCoordValue(coords.width, scalingFactor),
    height: transformToOriginalCoordValue(coords.height, scalingFactor),
  }
}

function getCanvasCoords(coords, scalingFactor) {
  return {
    'x-coordinate': transformToCanvasCoordValue(
      coords['x-coordinate'],
      scalingFactor,
    ),
    'y-coordinate': transformToCanvasCoordValue(
      coords['y-coordinate'],
      scalingFactor,
    ),
    width: transformToCanvasCoordValue(coords.width, scalingFactor),
    height: transformToCanvasCoordValue(coords.height, scalingFactor),
  }
}

export default {
  setCanvasBackgroundImage,
  addRectangle,
  fetchCoords,
  setObjCoords,
  addText,
  removeObject,
  addCropRectangle,
  constrainScaledObjToBoundingRect,
  constrainMovedObjToBoundingRect,
  getCanvasCoords,
  getOriginalCoords,
}
